import { useEffect, useRef, useState } from "react";
import client from "libs/client";
import type { continentCountries } from "pages/productManagement/types";

export interface NonPartnerClubReferenceData {
	countries: { [key: string]: string };
	countriesWithContinents: continentCountries[];
	geographicalAreaList: { value: string; label: string }[];
	isReady: boolean;
}

/**
 * Lazily loads the reference data the non-partner ClubModal needs.
 * Fires once, the first time `enabled` is true; retries on the next enable if it fails.
 */
export function useNonPartnerClubReferenceData(enabled: boolean): NonPartnerClubReferenceData {
	const [countries, setCountries] = useState<{ [key: string]: string }>({});
	const [countriesWithContinents, setCountriesWithContinents] = useState<continentCountries[]>([]);
	const [geographicalAreaList, setGeographicalAreaList] = useState<{ value: string; label: string }[]>([]);
	const [isReady, setIsReady] = useState(false);
	const loadedRef = useRef(false);

	useEffect(() => {
		if (!enabled || loadedRef.current) {
			return;
		}
		loadedRef.current = true;
		let cancelled = false;

		void (async () => {
			try {
				const [countriesRes, continentRes, geoRes] = await Promise.all([
					client.allCountries.query(),
					client.getContinentCountry.query(),
					client.getGeographicalAreaList.query(),
				]);
				if (cancelled) {
					return;
				}
				setCountries((countriesRes?.data ?? {}) as { [key: string]: string });
				setCountriesWithContinents((continentRes?.data ?? []) as continentCountries[]);
				setGeographicalAreaList((geoRes?.data ?? []) as { value: string; label: string }[]);
				setIsReady(true);
			} catch {
				// Allow a retry the next time the feature is enabled.
				loadedRef.current = false;
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [enabled]);

	return { countries, countriesWithContinents, geographicalAreaList, isReady };
}
